import React, { useState, useMemo, useRef } from "react";
import { toast } from "react-hot-toast";

import {
  Menu as MenuIcon,
  X as CloseIcon,
  Home,
  BedDouble,
  Calendar,
  Users,
  LogOut,
} from "lucide-react";

export default function LayoutFrontDeskSideBar({
  children,
}: {
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const mainRef = useRef<HTMLDivElement>(null);

  const username = localStorage.getItem("username") || "user1";
  const initials = useMemo(() => {
    const s = username.trim();
    if (!s) return "U";
    const parts = s.split(/[.\s_@-]+/).filter(Boolean);
    return (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase();
  }, [username]);

  const handleNavClick = (e: React.MouseEvent, href: string) => {
    e.preventDefault();
    setOpen(false);

    if (href === "#top") {
      mainRef.current?.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    const target = document.querySelector(href);
    if (target) (target as HTMLElement).scrollIntoView({ behavior: "smooth" });
  };

  const navItems = [
    { name: "Dashboard", icon: <Home size={18} />, href: "#top" },
    { name: "Rooms", icon: <BedDouble size={18} />, href: "#rooms" },
    {
      name: "Reservations",
      icon: <Calendar size={18} />,
      href: "#reservations",
    },
    { name: "Guests", icon: <Users size={18} />, href: "#guests" },
  ];

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("role");
    localStorage.removeItem("username");
    toast.success("Logout successful");
    setTimeout(() => (window.location.href = "/login"), 800);
  };

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Sidebar */}
      <aside
        className={`fixed z-20 inset-y-0 left-0 w-64 transform bg-white border-r shadow-lg transition-transform duration-200 ease-in-out ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between p-4 border-b">
          <h1 className="font-bold text-lg text-gray-700">HotelVPF</h1>
          <button
            className="p-2 text-gray-600 hover:text-gray-900"
            onClick={() => setOpen(false)}
          >
            <CloseIcon />
          </button>
        </div>

        <nav className="mt-4">
          {navItems.map((item) => (
            <a
              key={item.name}
              href={item.href}
              onClick={(e) => handleNavClick(e, item.href)}
              className="flex items-center gap-3 px-4 py-2 text-gray-700 hover:bg-gray-200"
            >
              {item.icon}
              {item.name}
            </a>
          ))}

          {/* Divider + Logout in sidebar */}
          <div className="mt-2 border-t pt-2">
            <button
              onClick={() => {
                setOpen(false); // închide meniul
                handleLogout(); // logout efectiv
              }}
              className="flex items-center gap-3 w-full px-4 py-2 text-red-600 hover:bg-red-50"
              title="Logout"
            >
              <LogOut size={18} />
              Logout
            </button>
          </div>
        </nav>
      </aside>

      {/* Main content + header */}
      <div className="flex-1 flex flex-col w-0">
        <header className="flex items-center justify-between bg-white p-4 border-b shadow">
          {/* Left: burger + brand */}
          <div className="flex items-center gap-3">
            <button
              className="p-2 text-gray-600 hover:text-gray-900"
              onClick={() => setOpen(!open)}
              title="Menu"
            >
              {open ? <CloseIcon /> : <MenuIcon />}
            </button>

            {/* 🔰 Brand moved next to menu icon */}
            <div className="flex items-center gap-2 group">
              <img
                src="/logos/frontdesk-logo.png"
                alt="Front Desk"
                width={45}
                height={45}
                className="block transition-all duration-300 drop-shadow-[0_0_6px_rgba(56,189,248,0.4)] group-hover:drop-shadow-[0_0_12px_rgba(56,189,248,0.7)]"
                onError={(e) =>
                  (e.currentTarget.src = "/logos/hotelvpf-tran-logo.png")
                }
              />
              <span className="px-3 py-1 text-sm font-medium rounded-full text-sky-700 bg-sky-50 border border-sky-100 transition-all duration-300 group-hover:shadow-[0_0_6px_rgba(56,189,248,0.5)]">
                Front Desk
              </span>
            </div>
          </div>

          {/* Right: user + logout */}
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex flex-col items-end leading-tight">
              <span className="block text-sm font-medium text-gray-800">
                {username || "user1"}
              </span>
              <span className="text-xs text-gray-500">Signed in</span>
            </div>

            <div
              title={username || "user1"}
              className="w-9 h-9 rounded-full bg-emerald-600 text-white font-bold flex items-center justify-center shadow-md shrink-0"
            >
              {initials}
            </div>

            <button
              onClick={handleLogout}
              className="inline-flex items-center gap-2 rounded-md bg-red-500 text-white px-3 py-1.5 hover:bg-red-600 shrink-0"
              title="Logout"
            >
              <LogOut size={16} />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </header>

        <main ref={mainRef} className="p-6 overflow-auto flex-1">
          <span id="top" />
          {children}
        </main>
      </div>
    </div>
  );
}
